import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiFileText, FiDownload, FiUserX, FiAlertTriangle, FiArrowRight, FiRefreshCw } from 'react-icons/fi';

export const SettingsSection = ({
  user,
  onExportData,
  onDeleteAccount,
  isExporting,
  isDeleting
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="space-y-6">
      {/* Account Settings */}
      <div className="bg-gray-800/50 border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden backdrop-blur-sm">
        <div className="px-6 py-5 border-b border-amber-500/10">
          <h2 className="text-xl font-bold text-white">
            Paramètres du compte
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            Gérez les paramètres de votre compte
          </p>
        </div>
        <div className="divide-y divide-amber-500/10">
          <div className="px-6 py-6">
            <div className="p-4 bg-gray-800/30 rounded-xl border border-amber-500/10">
              <h4 className="text-base font-medium text-white mb-4 flex items-center">
                <FiCreditCard className="mr-2 h-5 w-5 text-amber-400" />
                Informations de facturation
              </h4>
              <div className="bg-gray-800/50 p-4 rounded-lg border border-amber-500/10">
                <div className="flex items-center">
                  <div className="p-2.5 bg-amber-500/10 rounded-lg mr-3">
                    <FiCreditCard className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">
                      Carte de paiement
                    </p>
                    <p className="text-sm text-gray-400">
                      Visa se terminant par 4242
                    </p>
                  </div>
                  <button
                    type="button"
                    className="ml-auto text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Mettre à jour
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 pt-0">
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <h4 className="text-base font-medium text-white mb-4 flex items-center">
                <FiFileText className="mr-2 h-5 w-5 text-amber-400" />
                Abonnement
              </h4>
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="p-1.5 bg-amber-500/20 rounded-lg">
                    <FiFileText className="h-5 w-5 text-amber-400" />
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-amber-200">
                    Vous utilisez actuellement l'essai gratuit. Passez à un abonnement pour débloquer toutes les fonctionnalités.
                  </p>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="inline-flex items-center text-sm font-medium text-amber-300 hover:text-white transition-colors group"
                    >
                      Voir les plans
                      <FiArrowRight className="ml-1.5 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-gray-800/50 border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden backdrop-blur-sm">
        <div className="px-6 py-5 border-b border-amber-500/10">
          <h2 className="text-xl font-bold text-white">
            Gestion des données
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            Téléchargez ou supprimez vos données
          </p>
        </div>
        <div className="px-6 py-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-gray-800/30 rounded-xl border border-amber-500/10">
            <div className="flex-1">
              <h4 className="text-base font-medium text-white flex items-center">
                <FiDownload className="mr-2 h-5 w-5 text-amber-400" />
                Exporter mes données
              </h4>
              <p className="text-sm text-gray-400 mt-1">
                Téléchargez une copie de vos données personnelles
              </p>
            </div>
            <button
              type="button"
              onClick={onExportData}
              disabled={isExporting}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-600/90 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isExporting ? (
                <>
                  <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Export en cours...
                </>
              ) : (
                <>
                  <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                  Exporter
                </>
              )}
            </button>
          </div>

          <div className="p-4 bg-gray-800/30 rounded-xl border border-red-500/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-base font-medium text-red-400 flex items-center">
                  <FiUserX className="mr-2 h-5 w-5" />
                  Supprimer mon compte
                </h4>
                <p className="text-sm text-gray-400 mt-1">
                  Supprimez définitivement votre compte et toutes vos données
                </p>
              </div>
              
              {!showDeleteConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-600/90 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all w-full sm:w-auto"
                >
                  <FiUserX className="-ml-1 mr-2 h-4 w-4" />
                  Supprimer le compte
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-amber-500/30 text-sm font-medium rounded-lg text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={onDeleteAccount}
                    disabled={isDeleting}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDeleting ? (
                      <>
                        <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Suppression...
                      </>
                    ) : (
                      'Confirmer la suppression'
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {showDeleteConfirm && (
              <div className="mt-3 p-3 bg-red-900/20 border border-red-800/50 rounded-lg">
                <div className="flex">
                  <FiAlertTriangle className="flex-shrink-0 h-5 w-5 text-red-400 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-red-300">
                      Action irréversible
                    </h4>
                    <p className="text-sm text-red-400 mt-1">
                      Toutes vos données seront définitivement supprimées et cette action ne peut pas être annulée.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;
